import {AppRouterInstance} from "next/dist/shared/lib/app-router-context.shared-runtime";
import Cookies from 'js-cookie';
import {COOKIE_DATA} from "@/components/providers/appContext/types";
import {PASSWORD_ERROR} from "@/components/admin/login/types";
import {GENERAL_OBJECT} from "@/components/types";
import {BASE_PATH_NAME} from "@/components/constants";
import {SORT_PROPS} from "@/components/shared/tables/types";

const noRedirectUrls = ["login", "/login", "login/", "/login"];

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getRandomNumber = (data: { len?: number, type: string }) => {
    let len = data.len ?? 9;
    if (data.type === "uid") {
        len = 7;
    } else if (data.type === "code") {
        len = 6;
    } else if (data.type === "image") {
        len = 9;
    }
    let num = "1";
    for (let i = 1; i < len; i++) {
        num = `${num}0`;
    }
    return Math.floor(Number(num) + Math.random() * 9 * Number(num));
};

export function remToPx(value: string): number {
    return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value: number): string {
    return `${value / 16}rem`;
}

export const addComma = (val: string) => {
    while (/(\d+)(\d{3})/.test(val.toString())) {
        val = val.toString().replace(/(\d+)(\d{3})/, "$1" + "," + "$2");
    }
    return val;
}

export const detectIE = () => {
    let ua = window.navigator.userAgent;
    let msie = ua.indexOf('MSIE ');
    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }
    let trident = ua.indexOf('Trident/');
    if (trident > 0) {
        // IE 11 => return version number
        let rv = ua.indexOf('rv:');
        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }
    let edge = ua.indexOf('Edge/');
    if (edge > 0) {
        // Edge (IE 12+) => return version number
        return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }
    // other browser
    return false;
};

export const toRelativeUrl = (url: string) => {
    return "/" + url.replace(/^(?:\/\/|[^\/]+)*\//, "");
};

export const AsyncCookie = {
    setCookie: (cname: string, cvalue: string | number, exdays: number) => {
        const d = new Date();
        d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
        let expires = "expires=" + d.toUTCString();
        document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    },
    getCookie: (cname: string) => {
        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    },
    cookieExists: (name: string) => {
        const cks = document.cookie.split(';');
        return cks.filter(e => e.split('=')[0].trim() === name).length > 0;
    },
    deleteCookie: (cname: string) => {
        document.cookie = cname + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
};

export const getAllUrlParams = (str: string): string => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.get(str)) return urlParams.get(str)!
    return ""
}

export const redirectToLoginPage = () => {
    const pathName = window.location.pathname;
    if (!noRedirectUrls.includes(pathName)) {
        let redirectTo = pathName + window.location.search;
        if (window.location.search && window.location.search.indexOf("redirectTo") > -1) {
            redirectTo = getAllUrlParams("redirectTo")
        }
        window.location.href = `/login?redirectTo=${redirectTo.replaceAll("&", "%26")}`
    }
}

export const removeAppUrl = (str: string) => {
    return str.replaceAll('{appUrl}', '')
}

export const revokeCookieAndRedirectToLoginPage = (navigate: AppRouterInstance) => {
    AsyncCookie.deleteCookie("sessionId");
    navigate.push("/login")
}

export const handleRedirection = (navigate: AppRouterInstance, redirectTo: string) => {
    navigate.push(removeAppUrl(redirectTo))
}

export const getPromiseState = (promise: any, callback: (key: string) => void) => {
    const uniqueValue = window['Symbol'] ? Symbol('unique') : /unique/
    const notifyPendingOrResolved = (value: symbol | RegExp) => {
        if (value === uniqueValue) {
            return callback('pending')
        } else {
            return callback('fulfilled')
        }
    }
    const notifyRejected = () => {
        return callback('rejected')
    }
    const race = [promise, Promise.resolve(uniqueValue)]
    Promise.race(race).then(notifyPendingOrResolved, notifyRejected)
}

export const randomString = (len: number) => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz";
    let randomString = '';
    for (let i = 0; i < len; i++) {
        const num = Math.floor(Math.random() * characters.length);
        randomString += characters.substring(num, num + 1);
    }
    return randomString;
}

export const getRouterId = () => {
    const value: string | undefined = Cookies.get(process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "SITE_DATA");
    if (value) {
        const cookieData: COOKIE_DATA = JSON.parse(value);
        if (cookieData?.context?.webContext) {
            return cookieData.context.webContext.routerId
        }
    }
    return 1;
}

export const getRedirectToString = (str: string) => {
    const url = new URL(str);
    const params = new URLSearchParams(url.search);
    return params.get("redirectTo")
}

export const analyzeDataFromServer = (data: any) => {
    const {code, message, userLoginDetails} = data;
    const {sessionId, cookieData, redirectTo} = userLoginDetails || data;
    if ([300, 301].includes(code)) {
        return {
            error: true,
            message
        }
    }
    if (sessionId) {
        AsyncCookie.setCookie(`${process.env.NEXT_PUBLIC_COOKIE_PREFIX}_SESSION_ID`, sessionId, 365)
    }
    if (cookieData) {
        AsyncCookie.setCookie(`${process.env.NEXT_PUBLIC_COOKIE_PREFIX}_SITE_DATA`, JSON.stringify(cookieData), 365);
    }
    if (redirectTo) {
        return {
            redirect: true,
            redirectTo
        }
    }
    return userLoginDetails ?? data.data.data
}

export const resetCookieAndGoToLogin = (router: AppRouterInstance) => {
    const value: string | undefined = Cookies.get(process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "SITE_DATA");
    if (value) {
        const cookieData: COOKIE_DATA = JSON.parse(value);
        if (cookieData?.context?.webContext?.loginUrl) {
            AsyncCookie.deleteCookie(process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "SESSION_ID");
            const urlParams = new URLSearchParams(window.location.search);
            const redirectTo = urlParams.get('redirectTo');
            // window.location.href = `${cookieData.context.webContext.loginUrl}${!redirectTo ? '' : '?redirectTo=' + redirectTo}`;
            router.replace(`${cookieData.context.webContext.loginUrl}${!redirectTo ? '' : '?redirectTo=' + redirectTo}`)
        }
    }
}

export function validatePassword(password: string): PASSWORD_ERROR {
    const errors: PASSWORD_ERROR = {
        length: false,
        upperCase: false,
        special: false,
        number: false
    };

    if (password.length < 6) {
        errors["length"] = true;
    }

    if (!/[A-Z]/.test(password)) {
        errors["upperCase"] = true;
    }

    if (!/\d/.test(password)) {
        errors["number"] = true;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors["special"] = true;
    }

    return errors;
}

export const ensureLeadingSlash = (str: string) => str.startsWith('/') ? str : '/' + str;

export const formatDate = (date: Date, type?: number): string => {
    const day = String(date.getDate()).padStart(2, '0');    // 2 digits
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return type === 1 ? `${year}/${month}/${day}` : `${day}/${month}/${year}`;
};

export const getRecord = (recs: any[], val: string, keyForVal?: string, keyForName?: string): string => {
    const keyFName = keyForName ?? "label";
    const keyFVal = keyForVal ?? "value";
    const temp = recs.find((rec: any) => String(rec[keyFVal]) === val);
    if(temp) return temp[keyFName]
    return "";
}

const readableDate = (date: Date | string) => {
    if(typeof date === "string") {
        date = new Date(date)
    }
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
}

export const isValidDate = (date: Date | string | null) => {
    return date instanceof Date && !isNaN(date.getTime());
}

export const formatDateFromString = (date: Date) => {
    if(!isValidDate(date)) return "";
    return date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
}

export const checkAgeEligibility = (dobStr: string, sessionStartStr: string, ageLimitStr: string, relaxationStr: string) => {
    const dob = new Date(dobStr.split('/').reverse().join('-'));
    const sessionStartDate = new Date(sessionStartStr.split('/').reverse().join('-'));

    const [minAge, maxAge] = ageLimitStr.split('-').map(Number);
    const [relaxBelow, relaxAbove] = relaxationStr.split(',').map(Number);

    // Calculate actual age
    let ageYears = sessionStartDate.getFullYear() - dob.getFullYear();
    const sessionMonthDay = sessionStartDate.getMonth() * 100 + sessionStartDate.getDate();
    const dobMonthDay = dob.getMonth() * 100 + dob.getDate();
    if (sessionMonthDay < dobMonthDay) ageYears--;

    // Construct date range
    const minDob = new Date(sessionStartDate);
    minDob.setFullYear(minDob.getFullYear() - maxAge);
    minDob.setDate(minDob.getDate() - relaxAbove);

    const maxDob = new Date(sessionStartDate);
    maxDob.setFullYear(maxDob.getFullYear() - minAge);
    maxDob.setDate(maxDob.getDate() + relaxBelow);

    const eligible = dob >= minDob && dob <= maxDob;
    const localesData: GENERAL_OBJECT = {}

    let reason = '';
    if (!eligible) {
        if (dob < minDob) {
            reason = `studentIsOlder`;
            localesData.date = readableDate(maxDob)
        } else if (dob > maxDob) {
            reason = `studentIsYounger`;
            localesData.date = readableDate(minDob)
        } else {
            reason = 'dobNotInLimits';
        }
    }

    return {
        eligible,
        studentDOB: formatDateFromString(dob),
        sessionStartDate: formatDateFromString(sessionStartDate),
        studentAgeOnSessionStart: `${ageYears} years`,
        requiredAgeRange: `${minAge}-${maxAge} years`,
        relaxationApplied: `${relaxBelow} days below, ${relaxAbove} days above`,
        minDOBAllowed: formatDateFromString(minDob),
        maxDOBAllowed: formatDateFromString(maxDob),
        reason: eligible ? 'Eligible' : reason,
        localesData
    };
}

export const normalizeRedirectPath = (redirectTo: string) => {
    if (!redirectTo) return '/';
    redirectTo = redirectTo.replace(/^\/+/, '');
    if (redirectTo.startsWith(`${BASE_PATH_NAME}/`)) {
        redirectTo = redirectTo.slice(BASE_PATH_NAME.length);
    }
    return `${redirectTo.replace(/^\/+/, '')}`;
}

export const cleanRedirectTo = (url: string): string => {
    const parsedUrl = new URL(url);
    const rawRedirectTo = parsedUrl.searchParams.get('redirectTo');
    if (!rawRedirectTo) return parsedUrl.pathname;
    const cleanedRedirectTo = normalizeRedirectPath(rawRedirectTo.split('?')[0]);
    parsedUrl.searchParams.set('redirectTo', cleanedRedirectTo);
    return parsedUrl.pathname + '?' + parsedUrl.searchParams.toString();
}

export const downloadWithCustomName = async (url: string, filename: string) => {
    console.log(url, filename)
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
};

export const getSortObjectFromString = (str: string | null): SORT_PROPS[] | null => {
    if (str) {
        const parts = str.split(",");
        const result: SORT_PROPS[] = [];
        for (let i = 0; i < parts.length; i += 2) {
            result.push({
                field: parts[i],
                direction: parts[i + 1],
            });
        }
        return result;
    }
    return null
}

export const getSortString = (sortObject?: SORT_PROPS[] | null) => {
    if (sortObject?.length) {
        return sortObject.map(sort => `${sort.field},${sort.direction}`)
    }
    return null;
}